import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress
} from "@mui/material";
import { useState, useEffect } from "react";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { API_BASE_URL } from "../services/Constants";

export default function EnquiryDialog({ open, handleClose, product }) {
  const defaultQuantity = product === "Vermiwash" ? 50 : 1;

  const [form, setForm] = useState({
    name: "",
    phone: "",
    quantity: defaultQuantity,
    village: "",
    district: "",
    state: "",
    pincode: ""
  });

  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);

  // Reset quantity when product changes
  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      quantity: product === "Vermiwash" ? 50 : 1
    }));
  }, [product]);

  // Quantity Increment / Decrement
  const handleQuantityChange = (type) => {
    setForm((prev) => ({
      ...prev,
      quantity:
        type === "inc"
          ? Number(prev.quantity || 0) + 1
          : Math.max(1, Number(prev.quantity || 1) - 1)
    }));
  };

  // Validation
  const validateForm = () => {
    if (!form.name.trim()) return "Name is required";
    if (!form.phone.trim()) return "Phone number is required";
    if (!/^[0-9]{10}$/.test(form.phone))
      return "Enter valid 10 digit phone number";
    if (!form.village.trim()) return "Village is required";
    if (!form.quantity || Number(form.quantity) < 1)
      return "Minimum quantity should be 1";
    return null;
  };

  // Submit
  const handleSubmit = async () => {
    const error = validateForm();
    if (error) {
      setErrorMsg(error);
      return;
    }

    const payload = {
      name: form.name,
      phone: form.phone,
      product,
      quantity: Number(form.quantity),
      address: {
        village: form.village,
        district: form.district,
        state: form.state,
        pincode: form.pincode
      }
    };

    try {
      setLoading(true);

      const response = await fetch(
        `${API_BASE_URL}/enquiry/addEnquiry`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        throw new Error("Failed to submit enquiry");
      }

      // Open Success Dialog
      setSuccessDialogOpen(true);

      // Reset form
      setForm({
        name: "",
        phone: "",
        quantity: product === "Vermiwash" ? 50 : 1,
        village: "",
        district: "",
        state: "",
        pincode: ""
      });

    } catch (error) {
      setErrorMsg("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const quantityLabel =
    product === "Vermiwash"
      ? "How many Litres required?"
      : "How many Bags required? (50kg each)";

  return (
    <>
      {/* Main Enquiry Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{
            position: "relative",
            backgroundColor: "#1F4D2B",
            color: "#fff",
            textAlign: "center"
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Enquiry for {product}
          </Typography>

          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "#fff"
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>

          {/* Name */}
          <TextField
            fullWidth
            label="Name"
            margin="normal"
            value={form.name}
            onChange={(e) =>
              setForm({ ...form, name: e.target.value })
            }
          />

          {/* Phone */}
          <TextField
            fullWidth
            label="Phone Number"
            type="tel"
            margin="normal"
            value={form.phone}
            onChange={(e) =>
              setForm({ ...form, phone: e.target.value })
            }
          />

          {/* Quantity */}
          {product && (
            <Box mt={3}>
              <Typography fontWeight={600}>
                {quantityLabel}
              </Typography>

              <Box
                display="flex"
                alignItems="center"
                mt={1}
                sx={{
                  border: "1px solid #ddd",
                  borderRadius: 3,
                  width: "fit-content",
                  px: 1
                }}
              >
                {Number(form.quantity) > 1 && (
                  <IconButton
                    onClick={() => handleQuantityChange("dec")}
                  >
                    <RemoveIcon />
                  </IconButton>
                )}

                <TextField
                  value={form.quantity}
                  type="number"
                  variant="standard"
                  inputProps={{
                    min: 1,
                    style: { textAlign: "center", width: 80 }
                  }}
                  onChange={(e) => {
                    const value = e.target.value;

                    if (value === "") {
                      setForm({ ...form, quantity: "" });
                      return;
                    }

                    const numericValue = Number(value);

                    if (!isNaN(numericValue)) {
                      setForm({
                        ...form,
                        quantity:
                          numericValue < 1 ? 1 : numericValue
                      });
                    }
                  }}
                />

                <IconButton
                  onClick={() => handleQuantityChange("inc")}
                >
                  <AddIcon />
                </IconButton>
              </Box>
            </Box>
          )}

          {/* Address */}
          <Typography mt={4} fontWeight={700}>
            Address Details
          </Typography>

          <TextField
            fullWidth
            label="Village"
            margin="normal"
            value={form.village}
            onChange={(e) =>
              setForm({ ...form, village: e.target.value })
            }
          />

          <TextField
            fullWidth
            label="District"
            margin="normal"
            value={form.district}
            onChange={(e) =>
              setForm({ ...form, district: e.target.value })
            }
          />

          <TextField
            fullWidth
            label="State"
            margin="normal"
            value={form.state}
            onChange={(e) =>
              setForm({ ...form, state: e.target.value })
            }
          />

          <TextField
            fullWidth
            label="Pincode"
            margin="normal"
            value={form.pincode}
            onChange={(e) =>
              setForm({ ...form, pincode: e.target.value })
            }
          />

          {/* Submit */}
          <Button
            fullWidth
            variant="contained"
            sx={{ mt: 4, borderRadius: 3 }}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Submit Enquiry"
            )}
          </Button>

        </DialogContent>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        open={successDialogOpen}
        onClose={() => {
          setSuccessDialogOpen(false);
          handleClose();
        }}
      >
        <DialogContent sx={{ textAlign: "center", py: 4 }}>
          <CheckCircleIcon
            sx={{ fontSize: 70, color: "green", mb: 2 }}
          />
          <Typography variant="h6" fontWeight={700}>
            Enquiry Submitted Successfully!
          </Typography>
          <Typography mt={1}>
            RK Farm team will contact you soon.
          </Typography>

          <Button
            variant="contained"
            sx={{ mt: 3, borderRadius: 3 }}
            onClick={() => {
              setSuccessDialogOpen(false);
              handleClose();
            }}
          >
            OK
          </Button>
        </DialogContent>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={!!errorMsg}
        autoHideDuration={4000}
        onClose={() => setErrorMsg("")}
      >
        <Alert severity="error" variant="filled">
          {errorMsg}
        </Alert>
      </Snackbar>
    </>
  );
}
